import { Title as MantineTitle } from "@mantine/core";
import { componentWrapper } from "../../utilities";
import styles from "./title.module.scss";
import type { CustomTitleFactory, IProps, ISkeletonProps, ITitleCopyProps } from "./title.types";
import { Skeleton } from "../skeleton";
import { useDebouncedState, useDebouncedValue, useHover } from "@mantine/hooks";
import { CopyButton } from "../copy-button";
import classNames from "classnames";
import { Icon } from "../icon";
import { faCheck } from "@fortawesome/pro-solid-svg-icons";
import { faCopy } from "@fortawesome/pro-regular-svg-icons";
import { Group } from "../group";
import { Tooltip } from "../tooltip";

const Title = componentWrapper<CustomTitleFactory, IProps>("Title", (props, ref) => {
    return <MantineTitle ref={ref} classNames={{ root: styles["root"] }} {...props} />;
});

const TitleSkeleton = ({ order, width, visible, ...otherProps }: ISkeletonProps) => {
    return (
        <Skeleton
            className={styles["skeleton"]}
            data-order={order}
            width={visible ? width : undefined}
            visible={visible}
            {...otherProps}
        />
    );
};

const TitleCopy = ({ value, timeout, disableCopy, className, ...titleProps }: ITitleCopyProps) => {
    const { hovered, ref } = useHover<HTMLParagraphElement>();
    const [debouncedOpen] = useDebouncedValue(hovered, timeout ?? 900);
    const [debouncedCopied, setDebouncedCopied] = useDebouncedState(false, 200);

    return disableCopy ? (
        <Title {...titleProps} />
    ) : (
        <CopyButton value={value} timeout={timeout}>
            {({ copied, copy }) => {
                setDebouncedCopied(copied);
                return (
                    <Group justify="space-between" flex={1}>
                        <Title
                            ref={ref}
                            onClick={copy}
                            td={hovered ? "dotted" : undefined}
                            c={hovered ? "blue" : undefined}
                            className={classNames(styles["copy"], className)}
                            {...titleProps}
                        />
                        {(hovered || copied) && (
                            <Tooltip
                                opened={(hovered && debouncedOpen) || copied}
                                label={copied || debouncedCopied ? "Copied" : "Copy"}
                            >
                                <Icon
                                    size="xxs"
                                    color={copied || debouncedCopied ? "teal" : "gray"}
                                    icon={copied || debouncedCopied ? faCheck : faCopy}
                                />
                            </Tooltip>
                        )}
                    </Group>
                );
            }}
        </CopyButton>
    );
};

Title.Skeleton = TitleSkeleton;
Title.Copy = TitleCopy;

export { Title, TitleSkeleton, TitleCopy };
